import React from 'react';
import { fbCss } from './styles';
import { GeneratedSocialPostResult } from 'src/api/types/social-types';

type Maybe<T> = T | null | undefined;

export interface Counters {
  likes?: number;
  comments?: number;
  shares?: number;
}

export interface FacebookPostPreviewProps {
  post: GeneratedSocialPostResult;
  counters?: Counters;
  injectCss?: boolean; // set false if you include styles globally
  profileName?: string;
  profileAvatarUrl?: string;
  when?: string;
  linkPreview?: {
    url: string;
    image?: string;
    title?: string;
    description?: string;
  };
}

function linkify(text: string) {
  const urlRegex =
    /((https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/[^\s]*)?)/gi;

  const lines = text.split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {line.split(urlRegex).map((chunk, j) => {
        const looksLikeUrl =
          /^https?:\/\//i.test(chunk) || /^[a-z0-9-]+\.[a-z]{2,}/i.test(chunk);
        if (looksLikeUrl) {
          const href = /^https?:\/\//i.test(chunk) ? chunk : `https://${chunk}`;
          return (
            <a
              key={`${i}-${j}`}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="fbp-link">
              {chunk}
            </a>
          );
        }
        return <React.Fragment key={`${i}-${j}`}>{chunk}</React.Fragment>;
      })}
      {i < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
}

function domainFromUrl(url: Maybe<string>) {
  if (!url) return '';
  try {
    const u = new URL(url);
    return u.host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export default function FacebookPostPreview({
  post,
  counters,
  injectCss = true,
  profileName = 'Profile Name',
  profileAvatarUrl = 'https://upload.wikimedia.org/wikipedia/commons/2/2c/Default_pfp.svg',
  when = 'Just now',
  linkPreview,
}: FacebookPostPreviewProps) {
  const data = post.post;
  const likeCount = counters?.likes ?? 0;
  const commentCount = counters?.comments ?? 0;
  const shareCount = counters?.shares ?? 0;

  const hasImage = Boolean(post.imageLinkUrl || data.image?.link_url);
  const hasLinkPreview = Boolean(linkPreview?.url || data.cta?.url);

  const linkUrl = (linkPreview?.url || data.cta?.url || '').trim();
  const linkImg = linkPreview?.image;
  const linkTitle = linkPreview?.title || '';
  const linkDesc = linkPreview?.description || '';

  return (
    <>
      {injectCss && <style>{fbCss}</style>}

      <main className="fbp-container" role="main">
        <article className="fbp-card" aria-label="Facebook-like post preview">
          {/* Header */}
          <header className="fbp-header">
            <img src={profileAvatarUrl} alt="Profile" className="fbp-avatar" />
            <div className="fbp-header-main">
              <div className="fbp-profile-line">
                <a className="fbp-profile-name" role="button" tabIndex={0}>
                  {profileName}
                </a>
              </div>
              <div
                className="fbp-subline"
                aria-label="Timestamp and visibility">
                <span>{when}</span>
                <span aria-hidden="true">·</span>
                <svg
                  fill="currentColor"
                  aria-hidden="true"
                  viewBox="0 0 16 16"
                  className="fbp-icon-geo">
                  <g fillRule="evenodd" transform="translate(-448 -544)">
                    <g>
                      <path
                        d="M109.5 408.5c0 3.23-2.04 5.983-4.903 7.036l.07-.036c1.167-1 1.814-2.967 2-3.834.214-1 .303-1.3-.5-1.96-.31-.253-.677-.196-1.04-.476-.246-.19-.356-.59-.606-.73-.594-.337-1.107.11-1.954.223a2.666 2.666 0 0 1-1.15-.123c-.007 0-.007 0-.013-.004l-.083-.03c-.164-.082-.077-.206.006-.36h-.006c.086-.17.086-.376-.05-.529-.19-.214-.54-.214-.804-.224-.106-.003-.21 0-.313.004l-.003-.004c-.04 0-.084.004-.124.004h-.037c-.323.007-.666-.034-.893-.314-.263-.353-.29-.733.097-1.09.28-.26.863-.8 1.807-.22.603.37 1.166.667 1.666.5.33-.11.48-.303.094-.87a1.128 1.128 0 0 1-.214-.73c.067-.776.687-.84 1.164-1.2.466-.356.68-.943.546-1.457-.106-.413-.51-.873-1.28-1.01a7.49 7.49 0 0 1 6.524 7.434"
                        transform="translate(354 143.5)"
                      />
                      <path
                        d="M104.107 415.696A7.498 7.498 0 0 1 94.5 408.5a7.48 7.48 0 0 1 3.407-6.283 5.474 5.474 0 0 0-1.653 2.334c-.753 2.217-.217 4.075 2.29 4.075.833 0 1.4.561 1.333 2.375-.013.403.52 1.78 2.45 1.89.7.04 1.184 1.053 1.33 1.74.06.29.127.65.257.97a.174.174 0 0 0 .193.096"
                        transform="translate(354 143.5)"
                      />
                      <path
                        fillRule="nonzero"
                        d="M110 408.5a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-1 0a7 7 0 1 0-14 0 7 7 0 0 0 14 0z"
                        transform="translate(354 143.5)"
                      />
                    </g>
                  </g>
                </svg>
              </div>
            </div>

            <button aria-label="More" className="fbp-more" type="button">
              <svg viewBox="0 0 24 24" className="fbp-more-icon">
                <circle cx="5" cy="12" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="19" cy="12" r="2" />
              </svg>
            </button>
          </header>

          {/* Body */}
          <div className="fbp-body">{linkify(data.body || '')}</div>

          {/* Mentions */}
          {data.mentions?.length ? (
            <div className="fbp-mentions" aria-label="Mentions">
              {data.mentions.map(m => (
                <span key={m} className="fbp-mention">
                  {m}
                </span>
              ))}
            </div>
          ) : null}

          {/* Hashtags */}
          {data.hashtags?.length ? (
            <div className="fbp-hashtags" aria-label="Hashtags">
              {data.hashtags.map(tag => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          ) : null}

          {/* Image */}
          {hasImage && (
            <section className="fbp-image-wrap">
              <img
                src={post.imageLinkUrl || data.image?.link_url || ''}
                alt=""
                className="fbp-image"
              />
            </section>
          )}

          {/* Link preview */}
          {hasLinkPreview && (
            <a
              href={linkUrl}
              target="_blank"
              rel="noreferrer"
              className="fbp-link-card">
              {linkImg ? (
                <div className="fbp-link-card-media">
                  <img src={linkImg} alt="" className="fbp-link-card-img" />
                </div>
              ) : null}

              <div className="fbp-link-card-body">
                <div className="fbp-link-card-domain">
                  {domainFromUrl(linkUrl)}
                </div>
                <div className="fbp-link-card-title">
                  {linkTitle || linkUrl}
                </div>
                {linkDesc ? (
                  <div className="fbp-link-card-desc">{linkDesc}</div>
                ) : null}
              </div>
            </a>
          )}

          {/* Counters */}
          <div className="fbp-counters" aria-label="Reaction counts">
            <div className="fbp-counters-left">
              <span className="fbp-reaction-bg" style={{ zIndex: 2 }}>
                <img
                  className="fbp-reaction"
                  alt=""
                  height={18}
                  width={18}
                  src="data:image/svg+xml,%3Csvg fill='none' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M16.0001 7.9996c0 4.418-3.5815 7.9996-7.9995 7.9996S.001 12.4176.001 7.9996 3.5825 0 8.0006 0C12.4186 0 16 3.5815 16 7.9996Z' fill='url(%23paint0_linear_15251_63610)'/%3E%3Cpath d='M16.0001 7.9996c0 4.418-3.5815 7.9996-7.9995 7.9996S.001 12.4176.001 7.9996 3.5825 0 8.0006 0C12.4186 0 16 3.5815 16 7.9996Z' fill='url(%23paint1_radial_15251_63610)'/%3E%3Cpath d='M16.0001 7.9996c0 4.418-3.5815 7.9996-7.9995 7.9996S.001 12.4176.001 7.9996 3.5825 0 8.0006 0C12.4186 0 16 3.5815 16 7.9996Z' fill='url(%23paint2_radial_15251_63610)' fill-opacity='.5'/%3E%3Cpath d='M7.3014 3.8662a.6974.6974 0 0 1 .6974-.6977c.6742 0 1.2207.5465 1.2207 1.2206v1.7464a.101.101 0 0 0 .101.101h1.7953c.992 0 1.7232.9273 1.4917 1.892l-.4572 1.9047a2.301 2.301 0 0 1-2.2374 1.764H6.9185a.5752.5752 0 0 1-.5752-.5752V7.7384c0-.4168.097-.8278.2834-1.2005l.2856-.5712a3.6878 3.6878 0 0 0 .3893-1.6509l-.0002-.4496ZM4.367 7a.767.767 0 0 0-.7669.767v3.2598a.767.767 0 0 0 .767.767h.767a.3835.3835 0 0 0 .3835-.3835V7.3835A.3835.3835 0 0 0 5.134 7h-.767Z' fill='%23fff'/%3E%3Cdefs%3E%3CradialGradient id='paint1_radial_15251_63610' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='rotate(90 .0005 8) scale(7.99958)'%3E%3Cstop offset='.5618' stop-color='%230866FF' stop-opacity='0'/%3E%3Cstop offset='1' stop-color='%230866FF' stop-opacity='.1'/%3E%3C/radialGradient%3E%3CradialGradient id='paint2_radial_15251_63610' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='rotate(45 -4.5257 10.9237) scale(10.1818)'%3E%3Cstop offset='.3143' stop-color='%2302ADFC'/%3E%3Cstop offset='1' stop-color='%2302ADFC' stop-opacity='0'/%3E%3C/radialGradient%3E%3ClinearGradient id='paint0_linear_15251_63610' x1='2.3989' y1='2.3999' x2='13.5983' y2='13.5993' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2302ADFC'/%3E%3Cstop offset='.5' stop-color='%230866FF'/%3E%3Cstop offset='1' stop-color='%232B7EFF'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E"
                />
              </span>
              <img
                className="fbp-reaction-inline"
                alt=""
                height={18}
                width={18}
                src="data:image/svg+xml,%3Csvg fill='none' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cg clip-path='url(%23clip0_15251_63610)'%3E%3Cpath d='M15.9963 8c0 4.4179-3.5811 7.9993-7.9986 7.9993-4.4176 0-7.9987-3.5814-7.9987-7.9992 0-4.4179 3.5811-7.9992 7.9987-7.9992 4.4175 0 7.9986 3.5813 7.9986 7.9992Z' fill='url(%23paint0_linear_15251_63610)'/%3E%3Cpath d='M15.9973 7.9992c0 4.4178-3.5811 7.9992-7.9987 7.9992C3.5811 15.9984 0 12.417 0 7.9992S3.5811 0 7.9986 0c4.4176 0 7.9987 3.5814 7.9987 7.9992Z' fill='url(%23paint1_radial_15251_63610)'/%3E%3Cpath d='M7.9996 5.9081c-.3528-.8845-1.1936-1.507-2.1748-1.507-1.4323 0-2.4254 1.328-2.4254 2.6797 0 2.2718 2.3938 4.0094 4.0816 5.1589.3168.2157.7205.2157 1.0373 0 1.6878-1.1495 4.0815-2.8871 4.0815-5.159 0-1.3517-.993-2.6796-2.4254-2.6796-.9811 0-1.822.6225-2.1748 1.507Z' fill='%23fff'/%3E%3C/g%3E%3Cdefs%3E%3CradialGradient id='paint1_radial_15251_63610' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='matrix(0 7.9992 -7.99863 0 7.9986 7.9992)'%3E%3Cstop offset='.5637' stop-color='%23E11731' stop-opacity='0'/%3E%3Cstop offset='1' stop-color='%23E11731' stop-opacity='.1'/%3E%3C/radialGradient%3E%3ClinearGradient id='paint0_linear_15251_63610' x1='2.3986' y1='2.4007' x2='13.5975' y2='13.5993' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%23FF74AE'/%3E%3Cstop offset='.5001' stop-color='%23FA2E3E'/%3E%3Cstop offset='1' stop-color='%23FF5758'/%3E%3C/linearGradient%3E%3CclipPath id='clip0_15251_63610'%3E%3Cpath fill='%23fff' d='M-.001.0009h15.9992v15.9984H-.001z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E"
              />
              <span className="fbp-like-count">{likeCount}</span>
            </div>

            <div className="fbp-counters-right">
              <span>{commentCount} comments</span>
              <span>{shareCount} shares</span>
            </div>
          </div>

          <hr className="fbp-divider" />
        </article>
      </main>
    </>
  );
}
